// Kopf der Lead-Seite in einer ruhigen Zeile: Avatar, Name (Klick = umbenennen), Status-Pille und Score-Abzeichen
// direkt daneben, Menü „…“; keine Unterzeile (Design-Runde 2). Rechts schlanke Symbole mit Tooltip und Tastenkürzel:
// Anrufen ▾, E-Mail, SMS, Notiz, Follow-up ▾; groß nur „Custom Activity“ (öffnet das Menü mit den Typen 0–4).
// E-Mail und SMS öffnen die Karte über dem Feed (Komponenten aus dem Modul Kommunikation).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { leadsApi, type Aufgabe, type Lead, type LeadDetail } from "../../api";
import { useNachbarn } from "../../daten/liste";
import { useMeta } from "../../daten/meta";
import { useDialerMenuePunkte } from "../../telefonie";
import { T } from "../../texte";
import { kopieren } from "../../werkzeuge/tasten";
import { useWorkflowMenue } from "../../workflows";
import { FollowUpKnopf } from "../Aufgaben";
import { Auswahl } from "../Auswahl";
import { Avatar, ScoreAbzeichen, StatusPille } from "../Bausteine";
import { customWert } from "../../werkzeuge/felder";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { MenueKnopf, type MenuePunkt } from "../Menue";
import { telefonText, telefonTyp, useAnrufen } from "../Telefonnummer";
import { CustomActivityKnopf } from "./CustomActivityMenue";
import { useDublettenMenue } from "./dublettenMenue";
import { NOTIZ } from "./Eingabeleiste";
import { Eckdaten } from "./Eckdaten";
import { EMAIL, SMS } from "./KommunikationKarte";
import "../../styles/lead.css";

interface Props {
  detail: LeadDetail;
  onLead: (l: Lead) => void;
  onStatus: (statusId: string) => void;
  onAufgabe: (a: Aufgabe) => void;
  onNeuLaden: () => Promise<void>;
  onAnrufProtokoll: () => void;
  laedtNeu: boolean;
  /** offenes Eingabefeld (NOTIZ oder Typ-ID) */
  offen: string | null;
  onOeffnen: (was: string) => void;
  caMenue: boolean;
  setCaMenue: (o: boolean) => void;
  /** Zeitpunkt der neuesten Aktivität (aus dem Feed) */
  letzteAktivitaet?: string | null;
}

export function LeadKopf({ detail, onLead, onStatus, onAufgabe, onNeuLaden, onAnrufProtokoll, laedtNeu, offen, onOeffnen, caMenue, setCaMenue, letzteAktivitaet }: Props) {
  const meta = useMeta();
  const meldung = useMeldung();
  const navigate = useNavigate();
  const anrufen = useAnrufen();
  const { lead, contacts } = detail;
  // Power Dialer starten: mit der Smart View, aus der man kam, sonst Auswahl der eigenen Smart Views
  const nachbarn = useNachbarn(lead.id);
  const herkunftId = nachbarn?.kontext.quelle === "smartview" ? /\/smart-views\/([^/?#]+)/.exec(nachbarn.kontext.pfad)?.[1] : undefined;
  const dialerPunkte = useDialerMenuePunkte(herkunftId && nachbarn ? { id: decodeURIComponent(herkunftId), name: nachbarn.kontext.titel } : null);
  const [umbenennen, setUmbenennen] = useState(false);
  const [name, setName] = useState(lead.display_name ?? lead.name ?? "");
  const status = meta.status(lead.status_id);
  const ausClose = !lead.id.includes("_crm_");
  const leadName = lead.display_name ?? lead.name ?? "";
  const workflows = useWorkflowMenue({ art: "lead", leadId: lead.id, leadName });
  const dubletten = useDublettenMenue({ leadId: lead.id, leadName }); // Zusammenführen und Löschen (Agent 7C)

  const nummern = contacts.flatMap((k) => k.phones.map((p) => ({ kontakt: k, telefon: p })));
  const erste = nummern[0];
  const mailAdressen = contacts.reduce((n, k) => n + (k.emails?.length ?? 0), 0);

  const umbenennenStarten = () => {
    setName(leadName);
    setUmbenennen(true);
  };

  const speichernName = async () => {
    setUmbenennen(false);
    const neu = name.trim();
    if (!neu || neu === (lead.display_name ?? lead.name)) {
      setName(leadName);
      return;
    }
    try {
      const r = await leadsApi.aendern(lead.id, { name: neu });
      onLead(r.lead);
      meldung.ok("Name geändert.");
    } catch (e) {
      setName(leadName);
      meldung.fehler(e, "Name nicht geändert");
    }
  };

  const anrufMenue: MenuePunkt[] = [
    ...(nummern.length ? [{ label: "Nummer wählen", ueberschrift: true } as MenuePunkt] : []),
    ...nummern.map<MenuePunkt>(({ kontakt, telefon }) => ({
      label: `${telefonText(telefon.phone, telefon.phone_formatted)}`,
      icon: "telefon",
      rechts: `${kontakt.name ?? ""}${telefon.type ? ` · ${telefonTyp(telefon.type)}` : ""}`,
      onClick: () => void anrufen(telefon.phone, lead.id, kontakt.id, { leadName, kontaktName: kontakt.name }),
    })),
    { label: T.lead.anrufProtokollieren, icon: "notiz", trenner: nummern.length > 0, onClick: onAnrufProtokoll },
    { label: "Power Dialer", ueberschrift: true, trenner: true },
    ...dialerPunkte.map((p, i) => (i === 0 ? { ...p, trenner: false } : p)),
  ];

  const mehrMenue: MenuePunkt[] = [
    { label: "Umbenennen", icon: "stift", onClick: umbenennenStarten },
    { label: laedtNeu ? "Wird geladen …" : T.lead.neuLaden, icon: "neu-laden", deaktiviert: laedtNeu, onClick: () => void onNeuLaden() },
    ...(ausClose ? [{ label: T.lead.inCloseOeffnen, icon: "extern", onClick: () => window.open(`https://app.close.com/lead/${lead.id}/`, "_blank", "noopener") } as MenuePunkt] : []),
    {
      label: T.lead.idKopieren,
      icon: "kopieren",
      onClick: () => void kopieren(lead.id).then((ok) => (ok ? meldung.ok("Lead-ID kopiert.") : meldung.zeige("fehler", "Kopieren hat nicht geklappt."))),
    },
    { label: "Alle Aktivitäten", icon: "aktivitaet", onClick: () => navigate(`/aktivitaeten/anrufe?lead=${encodeURIComponent(lead.id)}`) },
    { label: "Workflows", ueberschrift: true, trenner: true },
    ...workflows.punkte,
    ...dubletten.punkte,
  ];

  // Status-Pille und Score-Abzeichen stehen direkt neben dem Namen; keine Unterzeile mehr (Dilsar, 16.10.2026)
  const scoreFeld = (meta.meta?.custom_fields.lead ?? []).find((f) => /score/i.test(f.name));
  const scoreWert = scoreFeld ? customWert(lead, scoreFeld.id) : null;

  return (
    <header className="ls-kopf">
      {dubletten.dialoge}
      <Avatar name={leadName} id={lead.id} groesse={32} />
      <div className="ls-kopf__text">
        <div className="ls-kopf__zeile">
          {umbenennen ? (
            <input
              className="eingabe ls-kopf__eingabe"
              value={name}
              autoFocus
              aria-label="Lead-Name"
              onChange={(e) => setName(e.target.value)}
              onBlur={() => void speichernName()}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") {
                  e.preventDefault();
                  setName(leadName);
                  setUmbenennen(false);
                }
              }}
            />
          ) : (
            <h1 className="ls-kopf__name" title={`${leadName} (Klick: umbenennen)`} onClick={umbenennenStarten}>
              {leadName || "Ohne Namen"}
            </h1>
          )}
          <Auswahl
            id="lead-status"
            optionen={(meta.meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }))}
            wert={lead.status_id}
            onWahl={(w) => w && w !== lead.status_id && onStatus(String(w))}
            knopfKlasse="ls-kopf__status"
            titel="Status ändern (s)"
            breite={260}
            knopfInhalt={
              <>
                <StatusPille label={status?.label ?? lead.status_label ?? "Ohne Status"} farbe={status?.color} />
                <Icon name="chevron-unten" groesse={12} />
              </>
            }
          />
          <ScoreAbzeichen wert={scoreWert} />
          <Eckdaten lead={lead} letzteAktivitaet={letzteAktivitaet} />
          <MenueKnopf knopfKlasse="symbolknopf symbolknopf--klein ls-kopf__mehr" titel="Lead verwalten" punkte={mehrMenue} breite={260}>
            <Icon name="mehr" groesse={15} />
          </MenueKnopf>
        </div>
      </div>
      <div className="ls-kopf__aktionen" role="toolbar" aria-label="Aktionen">
        <div className="knopfgruppe knopfgruppe--symbol">
          <button
            type="button"
            className="symbolknopf ls-aktion ls-aktion--anruf ls-kopf__knopf--anruf"
            disabled={!erste}
            aria-label="Anrufen"
            title={erste ? `Anrufen: ${erste.kontakt.name ?? ""} ${telefonText(erste.telefon.phone, erste.telefon.phone_formatted)} (c)` : "Keine Telefonnummer hinterlegt"}
            onClick={() => erste && void anrufen(erste.telefon.phone, lead.id, erste.kontakt.id, { leadName, kontaktName: erste.kontakt.name })}
            data-anrufen=""
          >
            <Icon name="telefon" groesse={16} />
          </button>
          <MenueKnopf knopfKlasse="symbolknopf ls-aktion ls-aktion--pfeil" titel="Weitere Nummern, Power Dialer, Anruf protokollieren" punkte={anrufMenue} ausrichtung="end" breite={340}>
            <Icon name="chevron-unten" groesse={13} />
          </MenueKnopf>
        </div>
        <button
          type="button"
          className={`symbolknopf ls-aktion${offen === EMAIL ? " ls-knopf--an" : ""}`}
          aria-label="E-Mail schreiben"
          title={mailAdressen ? "E-Mail schreiben (e)" : "E-Mail schreiben (e) · kein Kontakt mit E-Mail-Adresse"}
          aria-pressed={offen === EMAIL}
          onClick={() => onOeffnen(EMAIL)}
        >
          <Icon name="mail" groesse={16} />
        </button>
        <button
          type="button"
          className={`symbolknopf ls-aktion${offen === SMS ? " ls-knopf--an" : ""}`}
          aria-label="SMS schreiben"
          title={nummern.length ? "SMS schreiben (t)" : "SMS schreiben (t) · keine Telefonnummer hinterlegt"}
          aria-pressed={offen === SMS}
          onClick={() => onOeffnen(SMS)}
        >
          <Icon name="sms" groesse={16} />
        </button>
        <button
          type="button"
          className={`symbolknopf ls-aktion${offen === NOTIZ ? " ls-knopf--an" : ""}`}
          aria-label="Notiz schreiben"
          title="Notiz schreiben (n)"
          aria-pressed={offen === NOTIZ}
          onClick={() => onOeffnen(NOTIZ)}
        >
          <Icon name="notiz" groesse={16} />
        </button>
        <FollowUpKnopf leadId={lead.id} kontakte={contacts} onAngelegt={onAufgabe} symbol />
        <CustomActivityKnopf leadId={lead.id} offen={caMenue} setOffen={setCaMenue} onWahl={onOeffnen} aktiv={offen} />
      </div>
      {workflows.dialoge}
    </header>
  );
}
